/**
 * SMS — i18n 初始化（zh-HK 主／en／zh-CN）
 *
 * 硬規則：文字一律走 i18n（`rules.md` 通用章）；三份 locale 嘅 key 必須一致
 * （P1-4 vendor `@coeldery/parity-guard` 之後會有機器守衛；現階段靠人手對齊）。
 *
 * 為何 3 語：供應商多為內地廠家（zh-CN）；物流／JHC 用 zh-HK；en 作預備。
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import zhHK from './locales/zh-HK.json';
import en from './locales/en.json';
import zhCN from './locales/zh-CN.json';

export const SUPPORTED_LANGS = ['zh-HK', 'en', 'zh-CN'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const STORAGE_KEY = 'sms.lang';

/** 由瀏覽器語言揀一個支援嘅語言（搵唔到 → zh-HK） */
export function detectLang(nav: string | undefined): SupportedLang {
  const raw = (nav ?? '').toLowerCase();
  if (raw.startsWith('zh')) {
    return raw.includes('cn') || raw.includes('hans') ? 'zh-CN' : 'zh-HK';
  }
  if (raw.startsWith('en')) return 'en';
  return 'zh-HK';
}

/** 讀上次選擇（localStorage 唔可用時靜靜回退） */
function preferredLang(): SupportedLang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LANGS as readonly string[]).includes(saved)) return saved as SupportedLang;
  } catch {
    /* 私密模式／停用 storage → 用偵測值 */
  }
  return detectLang(typeof navigator === 'undefined' ? undefined : navigator.language);
}

export const getCurrentLang = (): SupportedLang => preferredLang();

export async function setLang(lang: SupportedLang): Promise<void> {
  await i18n.changeLanguage(lang);
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* 忽略 */
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    'zh-HK': { translation: zhHK },
    en: { translation: en },
    'zh-CN': { translation: zhCN },
  },
  lng: preferredLang(),
  fallbackLng: 'zh-HK',
  interpolation: { escapeValue: false },
});

export default i18n;
